import { useEffect, useState } from 'react';
import { useTableSelector } from 'hooks/useTableSelector';

import TableShutdownBanner, { TABLE_SHUTDOWN_MESSAGE } from './TableShutdownBanner';

import styles from './TableShutdownBanner.module.scss';

/**
 * EN-10249 AC2 — the shutdown notice for ONE cell of the multi-table grid.
 *
 * WHY THIS IS A SEPARATE COMPONENT AND NOT A PROP ON TableHeader.
 *
 * The grid mounts `<TableHeader compact />` ONCE, above the cells and OUTSIDE every
 * `TableIdProvider`. `useTableSelector` therefore falls back to `state.currentTable`
 * there — which is written only for the focused table — so a banner rendered in that
 * header would either show nothing or, worse, show the notice for whichever table
 * happened to be focused while sitting above all of them. AC2 says the notice is shown
 * "only to players seated at affected tables"; a header-level banner cannot satisfy
 * that, which is why the grid cohort was never notified at all.
 *
 * Mounting per cell, inside the cell's own provider, is what makes the scoping correct:
 * each instance resolves its own gameID and each flagged table gets exactly one banner.
 *
 * Renders nothing unless this specific table is flagged, so it is safe to mount in
 * every cell unconditionally.
 */
const GridTableShutdownBanner = () => {
  const closingAfterHand = useTableSelector((table) => table.game?.closing_after_hand);
  const isClosingAfterHand = Number(closingAfterHand) === 1;
  const [shutdownLatched, setShutdownLatched] = useState(isClosingAfterHand);

  // Some in-hand grid updates briefly report closing_after_hand=0 before the
  // next full table frame restores it. Once shutdown starts, keep the notice
  // visible for this mounted table; the cell is unmounted when closure finishes.
  useEffect(() => {
    if (isClosingAfterHand) setShutdownLatched(true);
  }, [isClosingAfterHand]);

  if (!isClosingAfterHand && !shutdownLatched) return null;

  return (
    <div className={styles.gridOverlay}>
      <TableShutdownBanner message={TABLE_SHUTDOWN_MESSAGE} />
    </div>
  );
};

export default GridTableShutdownBanner;
